/**
 * Supported language codes for multilingual text.
 */
export const LANGUAGE_CODES = [
  'en-US',
  'es-ES',
  'fr-FR',
  'de-DE',
  'it-IT',
  'pt-BR',
  'ar-SA',
] as const;

export type LanguageCode = (typeof LANGUAGE_CODES)[number];

/**
 * Multilingual text structure mapping language codes to text content.
 */
export type MultilingualText = Partial<Record<LanguageCode, string>>;

/**
 * Language metadata for display purposes.
 */
export type LanguageInfo = {
  code: LanguageCode;
  label: string;
  flag: string;
};

/**
 * Available languages with display information.
 */
export const LANGUAGES: LanguageInfo[] = [
  { code: 'en-US', label: 'English', flag: '🇺🇸' },
  { code: 'es-ES', label: 'Spanish', flag: '🇪🇸' },
  { code: 'fr-FR', label: 'French', flag: '🇫🇷' },
  { code: 'de-DE', label: 'German', flag: '🇩🇪' },
  { code: 'it-IT', label: 'Italian', flag: '🇮🇹' },
  { code: 'pt-BR', label: 'Portuguese', flag: '🇧🇷' },
  { code: 'ar-SA', label: 'Arabic', flag: '🇸🇦' },
];

/**
 * Get language info by code.
 */
export const getLanguageInfo = (code: LanguageCode): LanguageInfo | undefined =>
  LANGUAGES.find((lang) => lang.code === code);

/**
 * Check if a string is a valid language code.
 */
export const isValidLanguageCode = (code: string): code is LanguageCode =>
  LANGUAGE_CODES.includes(code as LanguageCode);
